/**
 * SearchResults - Lista de resultados de búsqueda avanzada
 */

import { useTranslation } from "react-i18next";
import { AlertCircle, SearchX, ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useApiError } from "@/hooks/use-api-error";
import type { BusquedaPaginada } from "@/hooks/use-busqueda-avanzada";
import { PatientRow } from "./patient-row";

interface SearchResultsProps {
  data: BusquedaPaginada | null;
  isLoading: boolean;
  error: Error | null;
  hasSearched: boolean;
  page: number;
  onPageChange: (page: number) => void;
  onRetry: () => void;
  onCloseSheet: () => void;
}

export function SearchResults({
  data,
  isLoading,
  error,
  hasSearched,
  page,
  onPageChange,
  onRetry,
  onCloseSheet,
}: SearchResultsProps) {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiError();

  // Loading state
  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="h-4 w-1/3" />
          </div>
        ))}
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center gap-3">
        <AlertCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-muted-foreground">{getErrorMessage(error)}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="mr-2 h-4 w-4" />
          {t("busquedaAvanzada.buttons.reintentar")}
        </Button>
      </div>
    );
  }

  // Initial state (no search yet)
  if (!hasSearched) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center gap-2">
        <SearchX className="h-8 w-8 text-muted-foreground/50" />
        <p className="text-sm text-muted-foreground">{t("busquedaAvanzada.states.initial")}</p>
      </div>
    );
  }

  // Empty results
  if (!data || data.results.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center gap-2">
        <SearchX className="h-8 w-8 text-muted-foreground/50" />
        <p className="text-sm font-medium">{t("busquedaAvanzada.states.empty")}</p>
        <p className="text-xs text-muted-foreground">
          {t("busquedaAvanzada.states.emptySuggestion")}
        </p>
      </div>
    );
  }

  const totalPages = Math.ceil(data.count / 10);

  return (
    <div className="flex flex-col">
      {/* Results count */}
      <div className="px-4 py-2 border-b bg-muted/30">
        <p className="text-xs text-muted-foreground">
          {t("busquedaAvanzada.results.count", { count: data.count })}
        </p>
      </div>

      {/* Patient rows */}
      <div className="divide-y">
        {data.results.map((paciente) => (
          <PatientRow key={paciente.id} paciente={paciente} onCloseSheet={onCloseSheet} />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t">
          <p className="text-xs text-muted-foreground">
            {t("busquedaAvanzada.results.page", { current: page, total: totalPages })}
          </p>
          <div className="flex gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={!data.previous}
              onClick={() => onPageChange(page - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-7 w-7"
              disabled={!data.next}
              onClick={() => onPageChange(page + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
